import { useState, useEffect } from 'react';
import { Menu, X, Moon, Sun } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import LanguageSwitcher from '../LanguageSwitcher';
import { languageService } from '../../js/languageService';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const [currentLanguage, setCurrentLanguage] = useState(languageService.getCurrentLanguage());
  
  useEffect(() => {
    const handleLanguageChange = (lang) => {
      setCurrentLanguage(lang);
      languageService.updatePageContent();
    };

    languageService.addListener(handleLanguageChange);
    document.addEventListener('languageChanged', handleLanguageChange);
    
    return () => {
      languageService.removeListener(handleLanguageChange);
      document.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, []);

  // Detect scroll to change header appearance
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  return (
    <header 
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled 
          ? 'glass-card border-b border-blue-500/10 py-3 shadow-md' 
          : 'bg-transparent backdrop-blur-sm py-5'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <a 
              href="https://quokkax.web.app/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            >
              <img 
                src="/quokka.png" 
                alt="QuokkaX Logo" 
                className="w-10 h-10 animate-float"
              />
              <span className="text-2xl font-bold gradient-heading" data-i18n="app.title">QuokkaX</span>
            </a>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            <a href="#home" className="card-text hover:text-primary transition-colors" data-i18n="nav.home">Home</a>
            <a href="#tokenomics" className="card-text hover:text-primary transition-colors" data-i18n="nav.tokenomics">Tokenomics</a>
            <a href="#presale" className="card-text hover:text-primary transition-colors" data-i18n="nav.presale">Presale</a>
            <a href="#roadmap" className="card-text hover:text-primary transition-colors" data-i18n="nav.roadmap">Roadmap</a>
            <a href="#whitepaper" className="card-text hover:text-primary transition-colors" data-i18n="nav.whitepaper">Whitepaper</a>
            <a href="#contact" className="card-text hover:text-primary transition-colors" data-i18n="nav.contact">Contact</a>
          </nav>
          
          {/* Theme Toggle, Language Switcher & Connect Wallet Button */}
          <div className="hidden md:flex items-center gap-4">
            <LanguageSwitcher />
            <button 
              onClick={toggleTheme}
              className="glass-card w-10 h-10 flex items-center justify-center rounded-full hover:bg-primary/10 transition-colors"
            >
              {theme === 'dark' ? <Sun size={18} className="text-primary" /> : <Moon size={18} className="text-primary" />}
            </button>
            
            <button className="gradient-button px-6 py-2 rounded-full text-white font-medium text-sm" data-i18n="nav.connect_wallet">
              Connect Wallet
            </button>
          </div>
          
          {/* Mobile Menu Button */}
          <div className="flex items-center gap-4 md:hidden">
            <LanguageSwitcher />
            <button 
              onClick={toggleTheme}
              className="glass-card w-10 h-10 flex items-center justify-center rounded-full hover:bg-primary/10 transition-colors"
            >
              {theme === 'dark' ? <Sun size={18} className="text-primary" /> : <Moon size={18} className="text-primary" />}
            </button>
            
            <button 
              className="text-primary"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile Navigation */}
      <div 
        className={`absolute top-full left-0 w-full glass-card border-t border-blue-500/10 transition-all duration-300 overflow-hidden ${
          mobileMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="container mx-auto px-4 py-4">
          <nav className="flex flex-col space-y-4">
            <a 
              href="#home" 
              className="card-text hover:text-primary transition-colors" 
              onClick={() => setMobileMenuOpen(false)}
              data-i18n="nav.home"
            >
              Home
            </a>
            <a 
              href="#tokenomics" 
              className="card-text hover:text-primary transition-colors"
              onClick={() => setMobileMenuOpen(false)}
              data-i18n="nav.tokenomics"
            >
              Tokenomics
            </a>
            <a 
              href="#presale" 
              className="card-text hover:text-primary transition-colors"
              onClick={() => setMobileMenuOpen(false)}
              data-i18n="nav.presale"
            >
              Presale
            </a>
            <a 
              href="#roadmap" 
              className="card-text hover:text-primary transition-colors"
              onClick={() => setMobileMenuOpen(false)}
              data-i18n="nav.roadmap"
            >
              Roadmap
            </a>
            <a 
              href="#whitepaper" 
              className="card-text hover:text-primary transition-colors"
              onClick={() => setMobileMenuOpen(false)}
              data-i18n="nav.whitepaper"
            >
              Whitepaper
            </a>
            <a 
              href="#contact" 
              className="card-text hover:text-primary transition-colors"
              onClick={() => setMobileMenuOpen(false)}
              data-i18n="nav.contact"
            >
              Contact
            </a>
            <button 
              className="gradient-button w-full py-2 rounded-full text-white font-medium text-sm"
              onClick={() => setMobileMenuOpen(false)}
              data-i18n="nav.connect_wallet"
            >
              Connect Wallet
            </button>
          </nav>
        </div>
      </div>
    </header>
  );
} 